/**
 * InstagramCarousel — 5 slide 1080×1350 per il lancio app, v2.
 *
 * iter v32 r37g (redo) · User M1435: «orribili a parte l'ultima. Mettile
 * con foto sfocate dietro come le card. Mockup iPhone 17 Pro. Riempile di
 * testo. Logo, watermark. Devono essere super.»
 *
 * Cambiamenti rispetto a v1:
 *  - Background a foto immobiliari blurate (filter: blur(40-60px))
 *  - Overlay color stratificati per leggibilità
 *  - Mockup iPhone 17 Pro accurato (titanio nero, Dynamic Island, frame
 *    sottile, ombre realistiche, camera bump suggerito)
 *  - Logo + watermark su ogni slide (top-left, bottom)
 *  - Più copy: ogni slide riempita di contenuto sostanziale
 *  - Slide 5 (CTA) mantenuto + logo/watermark consistente
 *
 * Visualizza: /share/carosello-lancio?slide=N (N 1-5) o senza param per
 * vedere tutte stack verticali.
 */
import React from "react";

const APP_STORE_URL = "https://apps.apple.com/it/app/prontoimmo-it/id6777127700";

const SLIDE_W = 1080;
const SLIDE_H = 1350;

const FAMILY_SERIF = "'Cormorant Garamond','Tinos','Times New Roman',Georgia,serif";
const FAMILY_SANS = "'Montserrat','Inter','Helvetica Neue',Helvetica,Arial,sans-serif";
const FAMILY_BODY = "'Manrope','Inter','Helvetica Neue',Helvetica,Arial,sans-serif";

/* ════════════════════════════════════════════════════════════════════
   SHARED · grain texture
   ════════════════════════════════════════════════════════════════════ */
function Grain({ opacity = 0.05 }) {
  return (
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        opacity,
        mixBlendMode: "overlay",
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
      }}
    />
  );
}

function BlurredBg({ src, blur = 60, brightness = 0.85, scale = 1.1 }) {
  return (
    <div
      aria-hidden
      className="absolute inset-0"
      style={{
        backgroundImage: `url(${src})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        filter: `blur(${blur}px) brightness(${brightness}) saturate(0.92)`,
        transform: `scale(${scale})`,
      }}
    />
  );
}

function BrandHeader({ tone = "dark" }) {
  const inkColor = tone === "dark" ? "#1C1C1A" : "#FAF6EE";
  const subColor = tone === "dark" ? "#5F6B53" : "rgba(250,246,238,0.7)";
  const filterStyle = tone === "dark" ? undefined : "brightness(0) invert(1)";
  return (
    <div
      className="absolute flex items-center gap-3"
      style={{ top: 50, left: 60 }}
    >
      <img
        src="/logo/mark-symbol-256.png"
        alt=""
        style={{ width: 46, height: 46, objectFit: "contain", filter: filterStyle, opacity: 0.95 }}
      />
      <div>
        <div
          style={{
            fontSize: 24,
            fontFamily: FAMILY_SANS,
            fontWeight: 500,
            letterSpacing: "-0.01em",
            color: inkColor,
            lineHeight: 1,
          }}
        >
          Pronto
        </div>
        <div
          style={{
            fontSize: 10,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: subColor,
            marginTop: 3,
          }}
        >
          Per Agenti Immobiliari
        </div>
      </div>
    </div>
  );
}

function Watermark({ tone = "dark", text = "prontoimmo.it" }) {
  const color = tone === "dark" ? "#1C1C1A" : "#FAF6EE";
  const sub = tone === "dark" ? "#6B6A65" : "rgba(250,246,238,0.55)";
  return (
    <div
      className="absolute"
      style={{
        bottom: 38,
        right: 60,
        textAlign: "right",
        fontFamily: FAMILY_SANS,
      }}
    >
      <div
        style={{
          fontFamily: FAMILY_SERIF,
          fontStyle: "italic",
          fontSize: 22,
          color,
          letterSpacing: "-0.005em",
        }}
      >
        {text}
      </div>
      <div
        style={{
          fontSize: 10,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color: sub,
          fontWeight: 600,
          marginTop: 4,
        }}
      >
        v1.0 · iOS
      </div>
    </div>
  );
}

function SlideNumber({ n, total = 5, tone = "dark" }) {
  const color = tone === "dark" ? "#6B6A65" : "rgba(250,246,238,0.6)";
  return (
    <div
      className="absolute"
      style={{
        top: 56,
        right: 60,
        fontSize: 11,
        letterSpacing: "0.32em",
        textTransform: "uppercase",
        fontWeight: 700,
        color,
        fontFamily: FAMILY_SANS,
      }}
    >
      {String(n).padStart(2, "0")} / {String(total).padStart(2, "0")}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   IPHONE 17 PRO · mockup accurato
   ════════════════════════════════════════════════════════════════════ */
function IPhone17Pro({ screen, width = 340, rotate = 0, shadow = true }) {
  const height = (width * 19.5) / 9.5; // proporzioni ~ 393×852
  return (
    <div
      style={{
        position: "relative",
        width,
        height,
        transform: `rotate(${rotate}deg)`,
        filter: shadow
          ? "drop-shadow(0 36px 60px rgba(0,0,0,0.34)) drop-shadow(0 14px 24px rgba(0,0,0,0.22))"
          : "none",
      }}
    >
      {/* Outer titanium frame */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: width * 0.18,
          background:
            "linear-gradient(140deg, #2A2A28 0%, #1A1A18 30%, #0F0F0D 70%, #1A1A18 100%)",
          padding: width * 0.026,
          boxShadow:
            "inset 0 0 0 1.5px rgba(255,255,255,0.08), inset 0 4px 14px rgba(0,0,0,0.6), inset 0 -2px 8px rgba(0,0,0,0.5)",
        }}
      >
        {/* Inner screen */}
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            borderRadius: width * 0.156,
            overflow: "hidden",
            background: "#FAFAF7",
          }}
        >
          {/* Dynamic Island */}
          <div
            style={{
              position: "absolute",
              top: width * 0.04,
              left: "50%",
              transform: "translateX(-50%)",
              width: width * 0.32,
              height: width * 0.085,
              background: "#000",
              borderRadius: width * 0.045,
              zIndex: 5,
              boxShadow: "0 0 0 1px rgba(255,255,255,0.04)",
            }}
          />
          {screen}
        </div>
      </div>
    </div>
  );
}

/* Reusable screen content · Sopralluogo (Slide 2) */
function SopralluogoScreen() {
  return (
    <div style={{ width: "100%", height: "100%", background: "#FAFAF7", overflow: "hidden" }}>
      <img
        src="/landing/demo-screens/sopralluogo.jpg"
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "top center",
        }}
      />
    </div>
  );
}

/* Vetrina screens for Slide 4 */
function ListingScreen() {
  return (
    <div style={{ width: "100%", height: "100%", background: "#FAFAF7", position: "relative", overflow: "hidden" }}>
      {/* Address bar */}
      <div style={{
        background: "#EDEAE2", padding: "16px 14px 8px", fontSize: 11,
        color: "#6B6A65", letterSpacing: "0.04em",
        fontFamily: FAMILY_SANS,
      }}>
        🔒 prontoimmo.it/listing/···
      </div>
      <div style={{ padding: "12px 0 0 0" }}>
        <img
          src="/landing/sala-original-after.jpg"
          alt=""
          style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }}
        />
      </div>
      <div style={{ padding: "16px 18px" }}>
        <div style={{
          fontSize: 9, color: "#7C8B6E", letterSpacing: "0.28em",
          textTransform: "uppercase", fontWeight: 700, marginBottom: 6,
        }}>
          In vendita · Brera, Milano
        </div>
        <div style={{
          fontFamily: FAMILY_SERIF, fontSize: 19, color: "#1C1C1A",
          lineHeight: 1.15, fontWeight: 500, marginBottom: 8,
        }}>
          Trilocale di pregio<br/>con terrazzo
        </div>
        <div style={{ fontSize: 11, color: "#6B6A65", marginBottom: 12, fontFamily: FAMILY_SANS }}>
          95 mq · 3° piano · €245.000
        </div>
        <div style={{
          background: "#1C1C1A", color: "#FAF6EE",
          padding: "8px 12px", borderRadius: 6,
          fontSize: 10, fontWeight: 600, letterSpacing: "0.12em",
          textTransform: "uppercase", display: "inline-block",
          fontFamily: FAMILY_SANS,
        }}>
          Contatta l&apos;agente
        </div>
      </div>
    </div>
  );
}

function AgentSiteScreen() {
  return (
    <div style={{ width: "100%", height: "100%", background: "#FAFAF7", position: "relative", overflow: "hidden" }}>
      {/* Address bar */}
      <div style={{
        background: "#EDEAE2", padding: "16px 14px 8px", fontSize: 11,
        color: "#6B6A65", letterSpacing: "0.04em",
        fontFamily: FAMILY_SANS,
      }}>
        🔒 prontoimmo.it/agente/···
      </div>
      <div style={{ padding: "26px 18px 16px", textAlign: "center" }}>
        {/* Agent avatar circle */}
        <div style={{
          width: 60, height: 60, borderRadius: 30, margin: "0 auto 14px",
          background: "linear-gradient(135deg, #7C8B6E 0%, #5F6B53 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#FAF6EE", fontSize: 22, fontFamily: FAMILY_SERIF, fontWeight: 500,
        }}>
          MR
        </div>
        <div style={{
          fontSize: 9, color: "#7C8B6E", letterSpacing: "0.32em",
          textTransform: "uppercase", fontWeight: 700, marginBottom: 6,
          fontFamily: FAMILY_SANS,
        }}>
          Agente immobiliare
        </div>
        <div style={{
          fontFamily: FAMILY_SERIF, fontSize: 22, color: "#1C1C1A",
          lineHeight: 1.05, fontWeight: 500, marginBottom: 4,
          letterSpacing: "-0.01em",
        }}>
          Luca Ferrari
        </div>
        <div style={{ fontSize: 11, color: "#6B6A65", fontStyle: "italic", marginBottom: 14, fontFamily: FAMILY_SERIF }}>
          Bergamo · dal 2014
        </div>
        <div style={{ fontSize: 10, color: "#5A5A52", lineHeight: 1.5, marginBottom: 16, fontFamily: FAMILY_SANS }}>
          Selezione di immobili in Città Alta e nella prima cintura.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          <img src="/landing/sala-original-after.jpg" alt="" style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", borderRadius: 4 }} />
          <img src="/landing/camera-after.jpg" alt="" style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", borderRadius: 4 }} />
        </div>
      </div>
    </div>
  );
}

/* Stima pubblica · usato in slide 3 (Stima online)
   Mockup della pagina /stima/{code}: range anonimo + unlock teaser +
   form preview che fa vedere chiaramente «chiediamo solo nome e telefono». */
function StimaPublicScreen() {
  return (
    <div style={{ width: "100%", height: "100%", background: "#FAFAF7", position: "relative", overflow: "hidden" }}>
      {/* Address bar */}
      <div style={{
        background: "#EDEAE2", padding: "14px 14px 7px", fontSize: 10,
        color: "#6B6A65", letterSpacing: "0.04em",
        fontFamily: FAMILY_SANS,
      }}>
        🔒 prontoimmo.it/stima/···
      </div>
      <div style={{ padding: "16px 18px" }}>
        <div style={{
          fontSize: 9, color: "#B57C5E", letterSpacing: "0.32em",
          textTransform: "uppercase", fontWeight: 700, marginBottom: 6,
          fontFamily: FAMILY_SANS,
        }}>
          La tua stima
        </div>
        <div style={{
          fontFamily: FAMILY_SERIF, fontSize: 16, color: "#1C1C1A",
          lineHeight: 1.15, fontWeight: 500, marginBottom: 3,
        }}>
          Via Solferino 18<br/>Bergamo
        </div>
        <div style={{ fontSize: 10, color: "#6B6A65", marginBottom: 11, fontFamily: FAMILY_SANS }}>
          95 mq · 3° piano · trilocale
        </div>
        {/* Big range */}
        <div style={{
          background: "#1C1C1A", color: "#FAF6EE",
          padding: "14px 13px", borderRadius: 10,
          marginBottom: 10,
        }}>
          <div style={{
            fontSize: 8, letterSpacing: "0.28em", textTransform: "uppercase",
            fontWeight: 700, color: "rgba(250,246,238,0.55)", marginBottom: 5,
            fontFamily: FAMILY_SANS,
          }}>
            Fascia indicativa
          </div>
          <div style={{
            fontFamily: FAMILY_SERIF, fontSize: 20, color: "#FAF6EE",
            lineHeight: 1.05, letterSpacing: "-0.02em", fontWeight: 500,
          }}>
            €245.000
            <span style={{ fontStyle: "italic", color: "#D4A282", marginLeft: 4, fontSize: 16 }}>
              — €285.000
            </span>
          </div>
        </div>
        {/* Unlock teaser con progress bar */}
        <div style={{
          background: "#F5F0E8", border: "1px solid #E1D7C7",
          padding: "10px 12px", borderRadius: 8, marginBottom: 10,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5 }}>
            <div style={{
              fontSize: 9, color: "#B57C5E", letterSpacing: "0.24em",
              textTransform: "uppercase", fontWeight: 700,
              fontFamily: FAMILY_SANS,
            }}>
              Stima dettagliata
            </div>
            <div style={{
              fontFamily: FAMILY_SERIF, fontStyle: "italic", fontSize: 13,
              color: "#B57C5E", lineHeight: 1,
            }}>
              pronta
            </div>
          </div>
          <div style={{
            height: 4, background: "#E1D7C7", borderRadius: 2,
            overflow: "hidden", marginBottom: 6,
          }}>
            <div style={{
              width: "100%", height: "100%", background: "#B57C5E",
            }} />
          </div>
          <div style={{ fontSize: 10, color: "#3F3D38", lineHeight: 1.35, fontFamily: FAMILY_SANS }}>
            Lascia nome e telefono per il prezzo preciso.
          </div>
        </div>
        {/* Form preview · nome + telefono */}
        <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 11 }}>
          <div style={{
            background: "#FFFFFF", border: "1px solid #E1D7C7",
            borderRadius: 7, padding: "8px 10px",
            fontSize: 11, color: "#1C1C1A",
            fontFamily: FAMILY_SANS,
          }}>
            Anna Belotti
          </div>
          <div style={{
            background: "#FFFFFF", border: "1px solid #E1D7C7",
            borderRadius: 7, padding: "8px 10px",
            fontSize: 11, color: "#1C1C1A",
            fontFamily: FAMILY_SANS,
          }}>
            +39 348 ···· ···
          </div>
        </div>
        {/* CTA */}
        <div style={{
          background: "#B57C5E", color: "#FAF6EE",
          padding: "10px 12px", borderRadius: 7,
          fontSize: 11, fontWeight: 700, letterSpacing: "0.14em",
          textTransform: "uppercase", textAlign: "center",
          fontFamily: FAMILY_SANS,
        }}>
          Ricevi la stima
        </div>
        {/* Trust line */}
        <div style={{
          fontSize: 9, color: "#9A9892", letterSpacing: "0.04em",
          textAlign: "center", marginTop: 7, marginBottom: 12, lineHeight: 1.35,
          fontFamily: FAMILY_SANS,
        }}>
          Riservato. No carta. No spam.
        </div>
        {/* Cosa trovi nella stima dettagliata */}
        <div style={{
          borderTop: "1px solid #EDEAE2", paddingTop: 11,
        }}>
          <div style={{
            fontSize: 8, color: "#9A9892", letterSpacing: "0.28em",
            textTransform: "uppercase", fontWeight: 700, marginBottom: 7,
            fontFamily: FAMILY_SANS,
          }}>
            Cosa trovi nel report
          </div>
          {[
            "Prezzo esatto stimato",
            "Cedolare e tasse",
            "3 immobili comparabili",
            "Scenario vendita",
          ].map((t) => (
            <div key={t} style={{
              display: "flex", alignItems: "center", gap: 7,
              marginBottom: 5,
            }}>
              <div style={{
                width: 11, height: 11, borderRadius: "50%",
                background: "#7C8B6E", display: "flex",
                alignItems: "center", justifyContent: "center",
                color: "#FAF6EE", fontSize: 7, fontWeight: 900,
                fontFamily: FAMILY_SANS, flexShrink: 0,
              }}>
                ✓
              </div>
              <div style={{
                fontSize: 10, color: "#3F3D38",
                fontFamily: FAMILY_SANS, lineHeight: 1.3,
              }}>
                {t}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Splash screen del logo · usato in slide 5 */
function LogoSplashScreen() {
  return (
    <div style={{
      width: "100%", height: "100%",
      background: "linear-gradient(180deg, #FAFAF7 0%, #F1F4ED 100%)",
      position: "relative", display: "flex",
      flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: 24,
    }}>
      <img
        src="/logo/mark-symbol-256.png"
        alt=""
        style={{ width: 110, height: 110, objectFit: "contain" }}
      />
      <div style={{
        fontFamily: FAMILY_SANS, fontSize: 38, fontWeight: 500,
        letterSpacing: "-0.015em", color: "#5F6B53", lineHeight: 1,
      }}>
        Pronto
      </div>
      <div style={{
        fontSize: 10, letterSpacing: "0.32em", textTransform: "uppercase",
        fontWeight: 700, color: "#7C8B6E", fontFamily: FAMILY_SANS,
      }}>
        Per Agenti Immobiliari
      </div>
      <div style={{
        position: "absolute", bottom: 24, left: 0, right: 0,
        textAlign: "center",
        fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase",
        fontWeight: 600, color: "#9A9892", fontFamily: FAMILY_SANS,
      }}>
        v1.0 · iOS
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SHARED · slide frame
   ════════════════════════════════════════════════════════════════════ */
function SlideFrame({ id, children }) {
  return (
    <div
      data-testid={`ig-slide-${id}`}
      style={{
        width: SLIDE_W,
        height: SLIDE_H,
        position: "relative",
        overflow: "hidden",
        background: "#0A0A09",
        fontFamily: FAMILY_SANS,
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SLIDE 1 · HERO
   iter v32 r37h · User M1436: usa testi landing «un'unica app per l'agente
   immobiliare» + concept giornata tipo. Foto camera col letto verde.
   ════════════════════════════════════════════════════════════════════ */
function Slide1() {
  return (
    <SlideFrame id="1">
      {/* BG · camera letto verde blurata */}
      <BlurredBg src="/landing/camera-after.jpg" blur={32} brightness={1.05} scale={1.15} />
      {/* Overlay cream warm — leggero per far traspirare la camera */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(165deg, rgba(250,246,238,0.74) 0%, rgba(250,246,238,0.55) 45%, rgba(241,244,237,0.74) 100%)",
        }}
      />
      <Grain opacity={0.06} />

      <BrandHeader tone="dark" />
      <SlideNumber n={1} tone="dark" />

      {/* Editorial claim · landing copy */}
      <div className="absolute" style={{ top: 220, left: 60, right: 60 }}>
        <div
          style={{
            fontSize: 12,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "#7C8B6E",
            marginBottom: 22,
          }}
        >
          Un&apos;unica app per l&apos;agente immobiliare
        </div>
        <h1
          style={{
            fontFamily: FAMILY_SERIF,
            fontWeight: 400,
            fontSize: 112,
            lineHeight: 0.96,
            letterSpacing: "-0.025em",
            margin: 0,
            color: "#1C1C1A",
          }}
        >
          Da un vocale
          <br />
          e qualche scatto.
        </h1>
        <div
          style={{
            fontFamily: FAMILY_SERIF,
            fontStyle: "italic",
            fontSize: 48,
            lineHeight: 1.05,
            letterSpacing: "-0.015em",
            marginTop: 22,
            color: "#7C8B6E",
            maxWidth: 720,
          }}
        >
          Esci dal sopralluogo
          <br />
          con tutto pronto.
        </div>
      </div>

      {/* Body copy · landing */}
      <div
        className="absolute"
        style={{
          top: 750,
          left: 60,
          right: 470,
          fontSize: 18,
          color: "#3F3D38",
          lineHeight: 1.55,
        }}
      >
        Parli mentre giri casa, scatti qualche foto col telefono.
        In pochi minuti hai annuncio per i portali, foto perfette
        e contenuti social pronti.
        <br />
        <br />
        <span style={{ color: "#1C1C1A", fontWeight: 700 }}>
          Tutto da una sola app. In italiano.
        </span>
      </div>

      {/* Giornata tipo · mini timeline */}
      <div
        className="absolute"
        style={{
          top: 970,
          left: 60,
          right: 470,
          paddingTop: 18,
          borderTop: "1px solid rgba(28,28,26,0.12)",
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "#7C8B6E",
            marginBottom: 12,
          }}
        >
          Una giornata tipo
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
          {[
            ["09:00", "Sopralluogo vocale"],
            ["11:30", "Foto perfette"],
            ["15:00", "Annuncio per i portali"],
            ["17:00", "Post social pronti"],
          ].map(([h, t]) => (
            <div key={h} style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
              <div style={{
                fontFamily: FAMILY_SERIF,
                fontStyle: "italic",
                fontSize: 18,
                color: "#7C8B6E",
                minWidth: 56,
              }}>
                {h}
              </div>
              <div style={{ fontSize: 14, color: "#3F3D38", fontWeight: 500 }}>
                {t}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* iPhone tilted right */}
      <div
        className="absolute"
        style={{ top: 380, right: -30 }}
      >
        <IPhone17Pro
          width={310}
          rotate={6}
          screen={<SopralluogoScreen />}
        />
      </div>

      {/* App Store badge bottom-left */}
      <div className="absolute" style={{ bottom: 100, left: 60 }}>
        <a href={APP_STORE_URL} style={{ display: "inline-block", textDecoration: "none" }}>
          <img
            src="/badges/app-store-it.svg"
            alt="Scarica su App Store"
            style={{
              width: 220,
              height: "auto",
              display: "block",
              filter: "drop-shadow(0 8px 18px rgba(28,28,26,0.18))",
            }}
          />
        </a>
      </div>

      <Watermark tone="dark" />
    </SlideFrame>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SLIDE 2 · LE FOTO
   iter v32 r37i · User M1438: rimossa la slide Sopralluogo (concetto già
   coperto da Slide 1) e promossa la slide Foto in posizione 2. La slide
   Stima online prende il posto 3.
   ════════════════════════════════════════════════════════════════════ */
function Slide2() {
  return (
    <SlideFrame id="2">
      <BlurredBg src="/landing/camera-after.jpg" blur={60} brightness={1.0} />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(165deg, rgba(250,246,238,0.86) 0%, rgba(251,241,234,0.74) 40%, rgba(250,246,238,0.85) 100%)",
        }}
      />
      <Grain opacity={0.05} />

      <BrandHeader tone="dark" />
      <SlideNumber n={2} tone="dark" />

      {/* Editorial */}
      <div className="absolute" style={{ top: 200, left: 60, right: 60 }}>
        <div
          style={{
            fontSize: 13,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "#B57C5E",
            marginBottom: 16,
          }}
        >
          01 · Le foto
        </div>
        <h2
          style={{
            fontFamily: FAMILY_SERIF,
            fontWeight: 400,
            fontSize: 86,
            lineHeight: 0.98,
            letterSpacing: "-0.02em",
            margin: 0,
            color: "#1C1C1A",
          }}
        >
          Una foto col telefono,
          <br />
          <span style={{ fontStyle: "italic", color: "#B57C5E" }}>
            una perfetta.
          </span>
        </h2>
        <div
          style={{
            marginTop: 18,
            fontSize: 17,
            color: "#3F3D38",
            lineHeight: 1.55,
            maxWidth: 880,
          }}
        >
          Scatta come fai sempre — qualunque luce, qualunque condizione.
          Pronto raddrizza la prospettiva, alza la luce, scioglie le ombre,
          riordina i piani. Stanze vuote diventano arredate. Piante catastali
          diventano leggibili e arredate.
        </div>
      </div>

      {/* Big before / after */}
      <div
        className="absolute"
        style={{
          top: 590,
          left: 60,
          right: 60,
          display: "flex",
          gap: 18,
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: "#9A9892",
              marginBottom: 8,
            }}
          >
            Prima · come l&apos;hai scattata
          </div>
          <div
            style={{
              borderRadius: 14,
              overflow: "hidden",
              aspectRatio: "4/5",
              background: "#EDEAE2",
            }}
          >
            <img
              src="/landing/camera-before.jpg"
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: "#7C8B6E",
              marginBottom: 8,
            }}
          >
            Dopo · 4 secondi · 1 click
          </div>
          <div
            style={{
              borderRadius: 14,
              overflow: "hidden",
              aspectRatio: "4/5",
              background: "#1C1C1A",
              boxShadow: "0 26px 50px rgba(28,28,26,0.20)",
            }}
          >
            <img
              src="/landing/camera-after.jpg"
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
        </div>
      </div>

      <div
        className="absolute"
        style={{
          bottom: 110,
          left: 60,
          right: 60,
          padding: "18px 22px",
          background: "rgba(255,255,255,0.78)",
          backdropFilter: "blur(8px)",
          border: "1px solid #EDEAE2",
          borderRadius: 12,
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "#B57C5E",
            marginBottom: 6,
          }}
        >
          Pronto sa fare anche
        </div>
        <div
          style={{
            fontSize: 15,
            color: "#1C1C1A",
            lineHeight: 1.45,
            fontFamily: FAMILY_SERIF,
          }}
        >
          Staging digitale per stanze vuote · planimetrie catastali
          rese leggibili e arredate · restyling esterno · sblocco
          della luce su foto in controluce.
        </div>
      </div>

      <Watermark tone="dark" />
    </SlideFrame>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SLIDE 3 · LA STIMA ONLINE DEL CLIENTE
   iter v32 r37j · User M1440: «3 non mi piace». La versione marrone dark
   non funzionava con il resto del carosello. Rifatta con palette cream +
   accent sage, stessa famiglia visiva di Slide 1, 2, 4.
   Testi dalla landing (BentoGridV2 «Stima online del cliente») adattati
   al "tu" del carosello.
   ════════════════════════════════════════════════════════════════════ */
function Slide3() {
  return (
    <SlideFrame id="3">
      {/* BG cream + sage soft */}
      <BlurredBg src="/landing/camera-after.jpg" blur={42} brightness={1.0} scale={1.12} />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(165deg, rgba(250,246,238,0.82) 0%, rgba(241,244,237,0.74) 50%, rgba(250,246,238,0.86) 100%)",
        }}
      />
      <Grain opacity={0.05} />

      <BrandHeader tone="dark" />
      <SlideNumber n={3} tone="dark" />

      {/* Editorial */}
      <div className="absolute" style={{ top: 200, left: 60, right: 60 }}>
        <div
          style={{
            fontSize: 13,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "#5F6B53",
            marginBottom: 18,
          }}
        >
          02 · Stima online del cliente
        </div>
        <h2
          style={{
            fontFamily: FAMILY_SERIF,
            fontWeight: 400,
            fontSize: 88,
            lineHeight: 0.96,
            letterSpacing: "-0.02em",
            margin: 0,
            color: "#1C1C1A",
          }}
        >
          «Quanto vale
          <br />
          casa mia?»
        </h2>
        <div
          style={{
            marginTop: 18,
            fontFamily: FAMILY_SERIF,
            fontStyle: "italic",
            fontSize: 40,
            lineHeight: 1.05,
            letterSpacing: "-0.015em",
            color: "#7C8B6E",
            maxWidth: 620,
          }}
        >
          Pronto risponde. Anonimo.
        </div>
        <div
          style={{
            marginTop: 26,
            fontSize: 18,
            color: "#3F3D38",
            lineHeight: 1.55,
            maxWidth: 580,
          }}
        >
          Mandi un tuo link personale al proprietario.
          Lui fa una <strong style={{ color: "#1C1C1A" }}>stima anonima
          in 6 domande dal telefono</strong>, senza lasciare nome o numero.
          <br /><br />
          Se vuole il dettaglio (cedolare, comparabili, scenario vendita)
          ti lascia il contatto. <strong style={{ color: "#1C1C1A" }}>
          Ricevi il lead già in rubrica.</strong>
        </div>
      </div>

      {/* iPhone con la pagina stima */}
      <div className="absolute" style={{ bottom: 80, right: 50 }}>
        <IPhone17Pro
          width={320}
          rotate={-3}
          screen={<StimaPublicScreen />}
        />
      </div>

      {/* 3 bullet points bottom-left */}
      <div className="absolute" style={{ bottom: 130, left: 60 }}>
        {[
          { l: "Link personale", s: "Lo mandi via WhatsApp" },
          { l: "6 domande", s: "Stima anonima dal telefono" },
          { l: "Lead in rubrica", s: "Solo se vuole il dettaglio" },
        ].map((b) => (
          <div
            key={b.l}
            style={{
              borderLeft: "2px solid #7C8B6E",
              paddingLeft: 14,
              marginBottom: 18,
            }}
          >
            <div
              style={{
                fontFamily: FAMILY_SERIF,
                fontStyle: "italic",
                fontSize: 22,
                color: "#1C1C1A",
                marginBottom: 2,
              }}
            >
              {b.l}
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#6B6A65",
                letterSpacing: "0.02em",
              }}
            >
              {b.s}
            </div>
          </div>
        ))}
      </div>

      <Watermark tone="dark" />
    </SlideFrame>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SLIDE 4 · LE VETRINE
   ════════════════════════════════════════════════════════════════════ */
function Slide4() {
  return (
    <SlideFrame id="4">
      {/* BG · camera blurata */}
      <BlurredBg src="/landing/camera-after.jpg" blur={70} brightness={0.85} />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(165deg, rgba(241,244,237,0.94) 0%, rgba(250,246,238,0.82) 50%, rgba(241,244,237,0.93) 100%)",
        }}
      />
      <Grain opacity={0.05} />

      <BrandHeader tone="dark" />
      <SlideNumber n={4} tone="dark" />

      {/* Editorial */}
      <div className="absolute" style={{ top: 190, left: 60, right: 60 }}>
        <div
          style={{
            fontSize: 13,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "#5F6B53",
            marginBottom: 16,
          }}
        >
          03 · Le tue vetrine
        </div>
        <h2
          style={{
            fontFamily: FAMILY_SERIF,
            fontWeight: 400,
            fontSize: 76,
            lineHeight: 0.98,
            letterSpacing: "-0.02em",
            margin: 0,
            color: "#1C1C1A",
          }}
        >
          Una pagina per ogni casa.
          <br />
          <span style={{ fontStyle: "italic", color: "#7C8B6E" }}>
            Un sito al tuo nome.
          </span>
        </h2>
        <div
          style={{
            marginTop: 16,
            fontSize: 16,
            color: "#3F3D38",
            lineHeight: 1.5,
            maxWidth: 920,
          }}
        >
          Pronto pubblica tutto per te. Ogni immobile che carichi ha la sua
          pagina pubblica condivisibile. In più, hai un mini-sito personale
          che raccoglie automaticamente i tuoi annunci attivi.
        </div>
      </div>

      {/* iter v32 r37h · User M1437: tolti i link badge sotto i mockup +
          tolta riga "il primo lo metti nei messaggi". Aggiunto invece body
          giustificato sotto ogni telefono con descrizioni dalla landing. */}
      <div
        className="absolute"
        style={{ top: 560, left: 60, right: 60, display: "flex", gap: 24 }}
      >
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: "#B57C5E",
              marginBottom: 14,
            }}
          >
            ① La pagina dell&apos;immobile
          </div>
          <IPhone17Pro
            width={230}
            rotate={-3}
            screen={<ListingScreen />}
          />
          <div
            style={{
              marginTop: 24,
              fontSize: 13,
              color: "#3F3D38",
              lineHeight: 1.55,
              textAlign: "justify",
              padding: "0 8px",
            }}
          >
            Foto perfette, planimetria arredata,
            descrizione completa e contatti. Si aggiorna
            da sola quando modifichi l&apos;annuncio. Pronta da
            inviare al cliente via WhatsApp o email.
          </div>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: "#5F6B53",
              marginBottom: 14,
            }}
          >
            ② Il sito al tuo nome
          </div>
          <IPhone17Pro
            width={230}
            rotate={3}
            screen={<AgentSiteScreen />}
          />
          <div
            style={{
              marginTop: 24,
              fontSize: 13,
              color: "#3F3D38",
              lineHeight: 1.55,
              textAlign: "justify",
              padding: "0 8px",
            }}
          >
            <span style={{ fontFamily: "'SF Mono',Menlo,monospace", fontSize: 12 }}>
              prontoimmo.it/agente/
            </span>
            il tuo nome. Bio, contatti, foto e tutti i tuoi
            immobili attivi, sempre aggiornati in automatico.
            Quattro estetiche, colore brand personalizzabile.
          </div>
        </div>
      </div>

      <Watermark tone="dark" />
    </SlideFrame>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SLIDE 5 · CTA FINALE (rivisto con logo + watermark)
   ════════════════════════════════════════════════════════════════════ */
function Slide5() {
  return (
    <SlideFrame id="5">
      {/* BG · camera col letto verde blurata (la più bella) */}
      <BlurredBg src="/landing/camera-after.jpg" blur={75} brightness={0.55} />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(165deg, rgba(28,28,26,0.78) 0%, rgba(38,52,33,0.70) 70%, rgba(20,28,17,0.86) 100%)",
        }}
      />
      <Grain opacity={0.08} />
      {/* decoro sage soft */}
      <div
        aria-hidden
        className="absolute"
        style={{
          bottom: -200,
          right: -180,
          width: 560,
          height: 560,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at center, rgba(168,184,154,0.20) 0%, rgba(168,184,154,0) 70%)",
        }}
      />

      <BrandHeader tone="light" />
      <SlideNumber n={5} tone="light" />

      {/* Hero */}
      <div className="absolute" style={{ top: 200, left: 60, right: 60 }}>
        <div
          style={{
            fontSize: 13,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "#A8B89A",
            marginBottom: 18,
          }}
        >
          Già disponibile · iOS
        </div>
        <h2
          style={{
            fontFamily: FAMILY_SERIF,
            fontWeight: 400,
            fontSize: 96,
            lineHeight: 0.93,
            letterSpacing: "-0.025em",
            margin: 0,
            color: "#FAF6EE",
          }}
        >
          Scarica
          <br />
          <span style={{ fontStyle: "italic", color: "#A8B89A" }}>
            l&apos;app.
          </span>
        </h2>
        <div
          style={{
            marginTop: 22,
            fontSize: 16,
            color: "rgba(250,246,238,0.82)",
            lineHeight: 1.55,
            maxWidth: 640,
          }}
        >
          L&apos;unica app per l&apos;agente immobiliare italiano.
          Tutto quello che ti serve, da un unico posto.
        </div>
      </div>

      {/* Lista completa servizi · 2 colonne · sotto al claim */}
      <div className="absolute" style={{ top: 560, left: 60, width: 600 }}>
        <div
          style={{
            fontSize: 12,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "#A8B89A",
            marginBottom: 18,
            paddingBottom: 14,
            borderBottom: "1px solid rgba(168,184,154,0.30)",
          }}
        >
          Tutto dentro Pronto
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 30px" }}>
          {[
            ["Sopralluogo vocale", "Detta mentre cammini"],
            ["Foto perfette", "Migliora, raddrizza, illumina"],
            ["Pianta arredata", "Catasto → render"],
            ["Annunci portali", "Idealista · Immobiliare"],
            ["Autopilot Portali", "Compila i form al posto tuo"],
            ["Vetrina immobile", "Pagina pubblica per ogni casa"],
            ["Sito personale", "prontoimmo.it/agente/tuo-nome"],
            ["Stima online del cliente", "Lead anonimo, già in rubrica"],
            ["Contenuti social", "Post Instagram pronti"],
            ["Agenda smart", "Sopralluoghi e visite"],
          ].map(([title, sub]) => (
            <div key={title}>
              <div
                style={{
                  fontFamily: FAMILY_SERIF,
                  fontWeight: 500,
                  fontSize: 18,
                  color: "#FAF6EE",
                  lineHeight: 1.2,
                  marginBottom: 3,
                }}
              >
                {title}
              </div>
              <div
                style={{
                  fontSize: 13.5,
                  color: "rgba(250,246,238,0.62)",
                  lineHeight: 1.35,
                }}
              >
                {sub}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* App Store badge */}
      <div className="absolute" style={{ bottom: 230, left: 60 }}>
        <a href={APP_STORE_URL} style={{ display: "inline-block", textDecoration: "none" }}>
          <img
            src="/badges/app-store-it.svg"
            alt="Scarica su App Store"
            style={{
              width: 280,
              height: "auto",
              display: "block",
              filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.45))",
            }}
          />
        </a>
      </div>

      {/* iPhone con logo splash */}
      <div className="absolute" style={{ top: 540, right: 50 }}>
        <IPhone17Pro
          width={250}
          rotate={6}
          screen={<LogoSplashScreen />}
        />
      </div>

      {/* Bottom info bar */}
      <div
        className="absolute"
        style={{
          bottom: 110,
          left: 60,
          right: 60,
          borderTop: "1px solid rgba(168,184,154,0.30)",
          paddingTop: 22,
        }}
      >
        <div
          style={{
            fontSize: 18,
            color: "rgba(250,246,238,0.85)",
            letterSpacing: "0.02em",
          }}
        >
          <strong style={{ color: "#FAF6EE" }}>Gratis</strong> · senza carta · 60 crediti al mese
        </div>
      </div>

      <Watermark tone="light" />
    </SlideFrame>
  );
}

/* ════════════════════════════════════════════════════════════════════
   PAGE · stack verticale o singola via ?slide=N
   ════════════════════════════════════════════════════════════════════ */
/* ════════════════════════════════════════════════════════════════════
   SLIDE STANDALONE · SCARICA L'APP (App Store hero) — v3
   iter v32 r37m · User M1443: «non mi piace, metti il logo dentro al
   cellulare, togli l'astina, usa i font giusti, scrivi meglio».
   Fix:
   - LogoSplashScreen dentro l'iPhone (la "scritta Pronto" del brand)
   - iPhone clean (rimosso camera bump + side button glints)
   - Cormorant Garamond per editorial, Manrope per body, Montserrat per
     wordmark Pronto (= esattamente i font del brand)
   - Copy invogliante senza ripetere il nome del brand 2 volte
   URL: /share/carosello-lancio?slide=app-store
   ════════════════════════════════════════════════════════════════════ */
function SlideAppStore() {
  return (
    <SlideFrame id="app-store">
      {/* BG · camera blurata cream (palette consistente) */}
      <BlurredBg src="/landing/camera-after.jpg" blur={36} brightness={1.06} scale={1.15} />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(155deg, rgba(250,246,238,0.80) 0%, rgba(241,244,237,0.72) 50%, rgba(250,246,238,0.86) 100%)",
        }}
      />
      {/* Soft sage halo dietro iPhone area */}
      <div
        aria-hidden
        className="absolute"
        style={{
          top: 380,
          right: -100,
          width: 700,
          height: 700,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at center, rgba(124,139,110,0.22) 0%, rgba(124,139,110,0.08) 40%, rgba(124,139,110,0) 70%)",
          filter: "blur(2px)",
        }}
      />
      <Grain opacity={0.05} />

      <BrandHeader tone="dark" />
      <div
        className="absolute"
        style={{
          top: 56,
          right: 60,
          fontSize: 11,
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          fontWeight: 700,
          color: "#7C8B6E",
          fontFamily: FAMILY_SANS,
        }}
      >
        v1.0 · iOS
      </div>

      {/* Editorial left */}
      <div className="absolute" style={{ top: 220, left: 60, right: 60 }}>
        <div
          style={{
            fontSize: 12,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "#7C8B6E",
            marginBottom: 22,
            fontFamily: FAMILY_SANS,
          }}
        >
          Già su App Store
        </div>
        <h1
          style={{
            fontFamily: FAMILY_SERIF,
            fontWeight: 400,
            fontSize: 132,
            lineHeight: 0.92,
            letterSpacing: "-0.028em",
            margin: 0,
            color: "#1C1C1A",
          }}
        >
          Tutto pronto.
          <br />
          <span style={{ fontStyle: "italic", color: "#7C8B6E" }}>
            Anche tu.
          </span>
        </h1>
      </div>

      {/* Body invogliante · Manrope, breve, benefit-driven */}
      <div
        className="absolute"
        style={{
          top: 700,
          left: 60,
          right: 470,
          fontFamily: FAMILY_BODY,
          fontSize: 18,
          color: "#3F3D38",
          lineHeight: 1.55,
        }}
      >
        Dal vocale al portale in pochi minuti.
        Foto perfette in 4 secondi.
        Vetrina pronta da inviare al cliente.
        Stima online che ti porta lead già in rubrica.
        <br />
        <br />
        <span style={{ color: "#1C1C1A", fontWeight: 700 }}>
          Una sera meno in ufficio. Una vendita in più.
        </span>
      </div>

      {/* App Store badge XL · CTA principale */}
      <div className="absolute" style={{ bottom: 200, left: 60 }}>
        <a
          href={APP_STORE_URL}
          style={{ display: "inline-block", textDecoration: "none" }}
        >
          <img
            src="/badges/app-store-it.svg"
            alt="Scarica su App Store"
            style={{
              width: 300,
              height: "auto",
              display: "block",
              filter: "drop-shadow(0 14px 32px rgba(28,28,26,0.22))",
            }}
          />
        </a>
      </div>

      {/* iPhone hero · destra, con logo splash Pronto sage brand */}
      <div className="absolute" style={{ top: 410, right: -10 }}>
        <IPhone17Pro
          width={360}
          rotate={6}
          screen={<LogoSplashScreen />}
        />
      </div>

      {/* Trust + watermark · bottom */}
      <div
        className="absolute"
        style={{
          bottom: 56,
          left: 60,
          right: 60,
          paddingTop: 22,
          borderTop: "1px solid rgba(28,28,26,0.12)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          fontFamily: FAMILY_BODY,
        }}
      >
        <div
          style={{
            fontSize: 15,
            color: "#3F3D38",
            letterSpacing: "0.02em",
          }}
        >
          <strong style={{ color: "#1C1C1A" }}>Gratis</strong>
          {" · senza carta · 60 crediti al mese"}
        </div>
        <div
          style={{
            fontFamily: FAMILY_SERIF,
            fontStyle: "italic",
            fontSize: 18,
            color: "#1C1C1A",
            letterSpacing: "-0.005em",
          }}
        >
          prontoimmo.it
        </div>
      </div>
    </SlideFrame>
  );
}

/* ════════════════════════════════════════════════════════════════════
   PAGE · stack verticale o singola via ?slide=N
   ════════════════════════════════════════════════════════════════════ */
export default function InstagramCarousel() {
  const params = new URLSearchParams(window.location.search);
  const single = params.get("slide");
  const slides = {
    "1": <Slide1 />, "2": <Slide2 />, "3": <Slide3 />, "4": <Slide4 />, "5": <Slide5 />,
    "app-store": <SlideAppStore />,
  };
  if (single && slides[single]) {
    return (
      <div className="flex items-center justify-center bg-[#0A0A09]" style={{ minHeight: "100vh", padding: 0 }}>
        {slides[single]}
      </div>
    );
  }
  return (
    <div
      className="flex flex-col items-center bg-[#0A0A09] py-10 gap-6"
      style={{ minHeight: "100vh" }}
    >
      <Slide1 />
      <Slide2 />
      <Slide3 />
      <Slide4 />
      <Slide5 />
      <SlideAppStore />
    </div>
  );
}
